// Copyright (c) Ping. All rights reserved.
//
// Start (or open) a 1:1 conversation with a peer BY ACCOUNT ID — the canonical
// web flow shared by the global-search "start chat", the Share modal, and the
// profile card/modal "Send message" action. Claims the peer's device
// key-packages + our own linked siblings, mints the MLS conversation, registers
// the local Dexie row, and (best-effort) sends a profile-intro so the peer's
// list row shows our name immediately.
//
// Returns the LOCAL conversation id (hex). Callers navigate to it (or, since
// `ChatRouteHost` resolves a route id by `peerAccountId`, to the account id).

import { log } from '@core/log/log';
import { claimAllDevicesByAccountId } from '@core/network';
import { retrieveIdentity } from '@core/persistence/identity';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { ensureConversation } from './ensureConversation';
import { getOrCreateMlsConversation } from './mlsConversation';

const CONV_META_PREFIX = 'ping:meta:v1:';

type ClientLike = Parameters<typeof getOrCreateMlsConversation>[0]['client'];

export interface StartDirectChatParams {
  readonly client: unknown;
  readonly accountId: string;
  readonly displayName: string;
}

export async function startDirectChatWithAccount(
  params: StartDirectChatParams,
): Promise<string> {
  const { client, accountId, displayName } = params;
  if (client === null) {
    throw new Error('Still signing you in. Try again in a moment.');
  }

  // Atomically consume one key-package per active device on the target.
  const claimed = await claimAllDevicesByAccountId({ accountId });
  const identity = await retrieveIdentity();

  // Claim our OWN other devices (excluding this one) so the new 1:1 is delivered
  // to every device of the account in the SAME Commit. Best-effort.
  const selfSiblingDevices = identity
    ? (
        await claimAllDevicesByAccountId({ accountId: identity.accountId }).catch(
          () => ({ devices: [] as typeof claimed.devices }),
        )
      ).devices
        .filter((d) => d.deviceId !== identity.deviceId)
        .map((d) => ({ deviceIdHex: d.deviceId, keyPackageBytes: d.keyPackage }))
    : [];

  const meta = {
    inviter: {
      accountId: identity?.accountId ?? '',
      displayName: identity?.displayName ?? 'Someone',
    },
    peer: { accountId: claimed.accountId, displayName },
  };

  const result = await getOrCreateMlsConversation({
    client: client as ClientLike,
    transport: getCurrentTransport(),
    beConversationId: `acct:${claimed.accountId}`,
    conversationName: `${CONV_META_PREFIX}${JSON.stringify(meta)}`,
    log,
    peerAccountId: claimed.accountId,
    preClaimedPeers: claimed.devices.map((d) => ({
      deviceIdHex: d.deviceId,
      keyPackageBytes: d.keyPackage,
    })),
    ...(selfSiblingDevices.length > 0 ? { selfSiblingDevices } : {}),
  });

  const localConvId = result.conversationIdHex;
  await ensureConversation({
    conversationId: localConvId,
    counterpartDisplayName: displayName,
    kind: 'direct',
    peerAccountId: claimed.accountId,
  });

  // Profile intro so the peer's row renders our name right away.
  if (result.inviteStatus.kind === 'ok') {
    try {
      const profile = {
        kind: 'ping.profile',
        payload: { accountId: meta.inviter.accountId, displayName: meta.inviter.displayName },
      };
      await result.conv.send(new TextEncoder().encode(JSON.stringify(profile)));
    } catch (err) {
      log.warn({ event: 'startDirectChat.profileIntro.failed', error: (err as Error).message });
    }
  }

  return localConvId;
}
